import {
    buildDataPresentationArtifact,
    matchesDataPresentation,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/bash_presentation_artifact_adapter';
import {
    type DynamicWidgetAdapter,
    type DynamicWidgetBuildContext,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_registry';
import {
    buildBindingPresentation,
    buildDynamicWidgetId,
    type CaregiverListWidgetModel,
    caregiverListWidgetModelSchema,
    type ClientDetailWidgetModel,
    clientDetailWidgetModelSchema,
    type ResourceShiftListWidgetModel,
    resourceShiftListWidgetModelSchema,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import { retrieveCaregiverListAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_caregiver_list_adapter';
import { retrieveClientDetailAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_client_detail_adapter';
import { retrieveShiftListAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_shift_list_adapter';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

export function buildDataShiftListWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): ResourceShiftListWidgetModel | null {
    const artifact = buildDataPresentationArtifact(entry, context, 'shifts');
    if (artifact === null || !entry.toolCallEventId || !entry.runId) {
        return null;
    }
    for (const [field, paths] of [
        ['shiftId', ['shift_id']],
        ['patientName', ['client/name']],
        ['day', ['start_time']],
        ['timeLabel', ['start_time', 'end_time']],
        ['status', ['status']],
        ['caregiverId', ['assigned_caregiver/id']],
        ['caregiverName', ['assigned_caregiver/name']],
        ['outreachState', ['outreach_state_summary']],
    ] as const) {
        artifact.builder.addLiveField({
            id: `resource.rows.${field}`,
            sourceIds: [artifact.resourceSourceId],
            paths: paths.map((path) => `/rows/*/${path}`),
            presentation: buildBindingPresentation({
                section: 'rows',
                label: field,
                role: field === 'shiftId' ? 'control' : 'field',
            }),
        });
    }
    return resourceShiftListWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'shifts'),
            templateVersion: 3,
            issues: [],
        }),
        template: 'shifts',
        templateVersion: 3,
        rows: [],
        dayGroups: [],
        note: null,
        rowCount: artifact.receipt.row_count,
        showHeaderCount: true,
        openShiftCount: 0,
        bulkOpenShiftCount: 0,
        bulkShiftIds: [],
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
        rowProvenance: 'resource',
    });
}

export function buildDataCaregiverListWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): CaregiverListWidgetModel | null {
    const artifact = buildDataPresentationArtifact(
        entry,
        context,
        'caregivers',
    );
    if (artifact === null || !entry.toolCallEventId || !entry.runId) {
        return null;
    }
    artifact.builder.addLiveField({
        id: 'resource.rows.fields',
        sourceIds: [artifact.resourceSourceId],
        paths: [
            '/rows/*/caregiver_id',
            '/rows/*/display_name',
            '/rows/*/active',
            '/rows/*/roles',
            '/rows/*/office',
            '/rows/*/city',
            '/rows/*/qualification_tags',
        ],
        presentation: buildBindingPresentation({
            section: 'rows',
            label: 'fields',
        }),
    });
    return caregiverListWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'entities'),
            templateVersion: 1,
            issues: [],
        }),
        template: 'entities',
        templateVersion: 1,
        variant: 'directory',
        rows: [],
        rowCount: artifact.receipt.row_count,
        rowProvenance: 'resource',
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
    });
}

export function buildDataClientDetailWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): ClientDetailWidgetModel | null {
    const artifact = buildDataPresentationArtifact(
        entry,
        context,
        'client_detail',
    );
    if (
        artifact === null ||
        artifact.receipt.row_count !== 1 ||
        !entry.toolCallEventId ||
        !entry.runId
    ) {
        return null;
    }
    artifact.builder.addLiveField({
        id: 'resource.record.fields',
        sourceIds: [artifact.resourceSourceId],
        paths: [
            '/rows/0/client_id',
            '/rows/0/display_name',
            '/rows/0/preferred_name',
            '/rows/0/office',
            '/rows/0/city',
            '/rows/0/state',
            '/rows/0/tags',
            '/rows/0/active',
        ],
        presentation: buildBindingPresentation({
            section: 'record',
            label: 'fields',
        }),
    });
    return clientDetailWidgetModelSchema.parse({
        ...artifact.builder.buildBase({
            widgetId: buildDynamicWidgetId(entry.toolCallEventId, 'detail'),
            templateVersion: 1,
            issues: [],
        }),
        template: 'detail',
        templateVersion: 1,
        record: null,
        rowCount: 1,
        rowProvenance: 'resource',
        workspaceFile: {
            runId: entry.runId,
            path: artifact.workspacePath,
        },
    });
}

export const dataShiftListAdapter: DynamicWidgetAdapter<ResourceShiftListWidgetModel> =
    {
        template: 'shifts',
        templateVersion: 3,
        toolNames: new Set(['run_bash', 'render_dynamic_ui', 'query']),
        matches: (entry) => matchesDataPresentation(entry, 'shifts'),
        build: buildDataShiftListWidgetModel,
        render(model, handlers) {
            // Saved results carry data provenance, not write authorization.
            // Keep action handlers outside this renderer even if retrieve
            // shift cards gain more actions later.
            return retrieveShiftListAdapter.render(model, {
                onCaregiverClick: handlers.onCaregiverClick,
                onClientClick: handlers.onClientClick,
                onPromptAction: handlers.onPromptAction,
            });
        },
    };

export const dataCaregiverListAdapter: DynamicWidgetAdapter<CaregiverListWidgetModel> =
    {
        template: 'entities',
        templateVersion: 1,
        toolNames: new Set(['run_bash', 'render_dynamic_ui', 'query']),
        matches: (entry) => matchesDataPresentation(entry, 'caregivers'),
        build: buildDataCaregiverListWidgetModel,
        render: retrieveCaregiverListAdapter.render,
    };

export const dataClientDetailAdapter: DynamicWidgetAdapter<ClientDetailWidgetModel> =
    {
        template: 'detail',
        templateVersion: 1,
        toolNames: new Set(['run_bash', 'render_dynamic_ui', 'query']),
        matches: (entry) => matchesDataPresentation(entry, 'client_detail'),
        build: buildDataClientDetailWidgetModel,
        render: retrieveClientDetailAdapter.render,
    };
